import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";
import { ApiResponse, BME_PART } from "@/api/types";
import { State as PlanState } from "@/store/pages/plan/types";
import { getEndOfMonth, fillZero } from "@/helpers/date-helper";

const planSettingsKey = "occ_planSettings";
const planGraduateYearsKey = "occ_planGraduateYears";

const PART_LENGTH_IN_MONTH = 3;
const PART_PERIOD: { [key in BME_PART]: { START: number; END: number } } = {
  [BME_PART.BEGINNING]: {
    START: 1,
    END: 10
  },
  [BME_PART.MIDDLE]: {
    START: 11,
    END: 20
  },
  [BME_PART.END]: {
    START: 21,
    END: -1 // 月によって異なるため、年月から算出する
  },
  [BME_PART.UNKNOWN]: {
    START: -1,
    END: -1
  }
};
// TODO: イベントに限らず、採用計画の開始日でも利用するため、名前を変更したい
export interface EventDate extends ApiResponse.BMEPeriodBase {
  day: number;
  date: dayjs.Dayjs;
}
const MONTHS_LENGTH = 16;
const SPEECH_BUBBLE_STYLES = {
  MARGIN: {
    VERTICAL: 10,
    HORIZONTAL: 10
  }
};
export const TIMELINE_STYLES = {
  PADDING: {
    TOP: 14,
    BOTTOM: 14
  }
};
export const EVENT_STYLES = {
  HEIGHT: 22,
  MARGIN: {
    TOP: 8,
    LEFT: 2,
    RIGHT: 2,
    BOTTOM: 0
  }
};

export type TimelineDate = {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
};
export type TimelineDateList = {
  [graduateYear: number]: TimelineDate;
};
type EventDateList = {
  [graduateYear: number]: EventDate;
};
export type CompetitorEventDate = {
  start: EventDateFromBME;
  end: EventDateFromBME;
};
type CompetitorEventDateList = {
  [eventId: number]: CompetitorEventDate;
};

export class CompetitorEvent {
  /*
  NOTE:
  他社イベント用ID: DB上ではイベントIDを持たないが、
  UI上はIDをリストのkeyとして利用するため、代替として利用する
  */
  private competitorEventId: number;
  constructor() {
    this.competitorEventId = 1;
  }
  init(event: ApiResponse.CompetitorEvent): Application.CompetitorEvent {
    if (event.start_at.part === BME_PART.UNKNOWN)
      event.start_at.part = BME_PART.BEGINNING;
    if (event.end_at.part === BME_PART.UNKNOWN)
      event.end_at.part = BME_PART.END;
    const startEventDate = getEventDateFromBME(event.start_at);
    const endEventDate = getEventDateFromBME(event.end_at);
    return {
      ...event,
      id: this.competitorEventId++,
      startDate: startEventDate.from.date,
      endDate: endEventDate.to.date,
      position: {
        width: 0,
        height: 0,
        x: 0,
        y: 0
      }
    };
  }
}

export class StudentEvent {
  /*
  NOTE:
  他社イベント用ID: DB上ではイベントIDを持たないが、
  UI上はIDをリストのkeyとして利用するため、代替として利用する
  */
  private competitorEventId: number;
  constructor() {
    this.competitorEventId = 1;
  }
  init(event: ApiResponse.StudentEvent): Application.StudentEvent {
    const startEventDate = getEventDateFromBME(event.start_at);
    const endEventDate = getEventDateFromBME(event.end_at);
    return {
      ...event,
      id: this.competitorEventId++,
      startDate: startEventDate.from.date,
      endDate: endEventDate.to.date,
      position: {
        width: 0,
        height: 0,
        x: 0,
        y: 0
      }
    };
  }
}

const competitorEvent = new CompetitorEvent();
const studentEvent = new StudentEvent();

export function getTimelineDateFromGraduateYear(
  graduateYear: number
): TimelineDate {
  return {
    start: dayjs(`${graduateYear - 2}-04-01`, "YYYY-MM-DD"),
    end: dayjs(`${graduateYear - 1}-08-31`, "YYYY-MM-DD")
  };
}

export function getTimelineDateListForCompetitors(
  competitorEvents: ApiResponse.CompetitorEvent[]
) {
  return competitorEvents.reduce<TimelineDateList>((accumulator, event) => {
    if (!accumulator[event.graduate_year]) {
      accumulator[event.graduate_year] = getTimelineDateFromGraduateYear(
        event.graduate_year
      );
    }
    return accumulator;
  }, {});
}

export function getTimelineDateListForStudents(
  studentEvents: ApiResponse.StudentEvent[],
  graduate_year: number
) {
  return studentEvents.reduce<TimelineDateList>(accumulator => {
    if (!accumulator[graduate_year]) {
      accumulator[graduate_year] = getTimelineDateFromGraduateYear(
        graduate_year
      );
    }
    return accumulator;
  }, {});
}

export function getTimelineStartEventDateList(planDateList: TimelineDateList) {
  return Object.keys(planDateList).reduce<EventDateList>(
    (accumulator, graduateYear) => {
      accumulator[Number(graduateYear)] = getEventDateFromDate(
        planDateList[Number(graduateYear)].start
      );
      return accumulator;
    },
    {}
  );
}

/*
NOTE:
他社のタイムラインは時間軸でイベントを並べるようになっているが、
卒年が異なる他社イベントを同じ時間軸に並べる必要があるため、
他社のイベントの年を今年基準（基準が同一であれば何でも良い）で統一し、
同じ時間軸の中でy座標を計算できるようにイベント情報を加工する
*/
export function unifyYearsOfCompetitorEvents(
  competitorEvents: Application.CompetitorEvent[]
): Application.CompetitorEvent[] {
  const clonedCompetitorEvents = cloneDeep(competitorEvents);
  const thisYear = dayjs().year();
  return clonedCompetitorEvents.map(event => {
    const gapOfStartDate = event.graduate_year - event.startDate.year();
    const gapOfEndDate = event.graduate_year - event.endDate.year();
    event.start_at.year = thisYear - gapOfStartDate;
    event.startDate = event.startDate.year(thisYear - gapOfStartDate);
    event.end_at.year = thisYear - gapOfEndDate;
    event.endDate = event.endDate.year(thisYear - gapOfEndDate);
    return event;
  });
}
export function unifyYearsOfStudentEvents(
  competitorEvents: Application.StudentEvent[],
  graduate_year: number
): Application.StudentEvent[] {
  const clonedCompetitorEvents = cloneDeep(competitorEvents);
  const thisYear = dayjs().year();
  return clonedCompetitorEvents.map(event => {
    const gapOfStartDate = graduate_year - event.startDate.year();
    const gapOfEndDate = graduate_year - event.endDate.year();
    event.start_at.year = thisYear - gapOfStartDate;
    event.startDate = event.startDate.year(thisYear - gapOfStartDate);
    event.end_at.year = thisYear - gapOfEndDate;
    event.endDate = event.endDate.year(thisYear - gapOfEndDate);
    return event;
  });
}

export function getTimeboxElement() {
  return document.querySelector(
    ".eventTimeline__timeline__scale__timebox"
  ) as HTMLElement;
}

export function getTimelineSizes(timebox: HTMLElement) {
  const timeboxWidth = timebox.getBoundingClientRect().width;
  const partWidth = timeboxWidth / PART_LENGTH_IN_MONTH;
  const timelineWidth = timeboxWidth * MONTHS_LENGTH;
  return {
    timeline: {
      width: timelineWidth
    },
    timebox: {
      width: timeboxWidth
    },
    part: {
      width: partWidth
    }
  };
}

type EventDateFromBME = {
  from: EventDate;
  to: EventDate;
};

export function getEventDateFromBME(
  bme: ApiResponse.BMEPeriodBase
): EventDateFromBME {
  let temporaryFromDay = -1;
  let temporaryToDay = -1;
  switch (bme.part) {
    case BME_PART.BEGINNING:
    case BME_PART.MIDDLE:
      temporaryFromDay = PART_PERIOD[bme.part].START;
      temporaryToDay = PART_PERIOD[bme.part].END;
      break;
    case BME_PART.END:
      temporaryFromDay = PART_PERIOD[bme.part].START;
      temporaryToDay = getEndOfMonth(bme.year, bme.month);
      break;
  }
  let temporaryFromDate = dayjs(
    `${bme.year}-${("0" + bme.month).slice(-2)}-${(
      "0" + temporaryFromDay
    ).slice(-2)}`,
    "YYYY-MM-DD"
  );
  let temporaryToDate = dayjs(
    `${bme.year}-${("0" + bme.month).slice(-2)}-${("0" + temporaryToDay).slice(
      -2
    )}`,
    "YYYY-MM-DD"
  );
  return {
    from: {
      ...bme,
      day: temporaryFromDay,
      date: temporaryFromDate
    },
    to: {
      ...bme,
      day: temporaryToDay,
      date: temporaryToDate
    }
  };
}

export function getEventDateFromDate(date: dayjs.Dayjs): EventDate {
  const part: BME_PART =
    date.date() < PART_PERIOD[BME_PART.MIDDLE].START
      ? BME_PART.BEGINNING
      : date.date() < PART_PERIOD[BME_PART.END].START
      ? BME_PART.MIDDLE
      : BME_PART.END;
  return {
    type: "BME",
    part,
    year: date.year(),
    month: date.month() + 1,
    day: date.date(),
    date
  };
}

export function isSamePeriod(date1: EventDate, date2: EventDate) {
  return (
    date1.year === date2.year &&
    date1.month === date2.month &&
    date1.part === date2.part
  );
}

/**
 * 2つのイベント日付を旬単位で未来かどうかを判定する関数
 *
 * @param date1 比較元のイベント日付
 * @param date2 比較先のイベント日付
 * @return {Boolean} 1つ目のイベント日付が２つ目のイベント日付より未来の場合はtrue、そうでなければfalse
 */
export function isFutureBME(eventDate1: EventDate, eventDate2: EventDate) {
  const isFutureDate = eventDate1.date.isAfter(eventDate2.date);
  const dateOfNextBME = getDateOfNextBME(eventDate2);
  if (!isFutureDate || !dateOfNextBME) {
    return false;
  }

  const isSame = eventDate1.date.isSame(dateOfNextBME);
  const isAfter = eventDate1.date.isAfter(dateOfNextBME);

  return isSame || isAfter;
}

/**
 * イベント日付をもとに１旬分未来の日付を取得する関数
 * @param eventDate イベント日付
 * @return {dayjs} 1旬分未来の日付
 */
export function getDateOfNextBME(eventDate: EventDate) {
  let year = eventDate.date.year();
  let month = eventDate.date.month() + 1;
  let nextDate: dayjs.Dayjs | null = null;
  switch (eventDate.part) {
    case BME_PART.BEGINNING:
      // NOTE: 初旬から中旬
      nextDate = dayjs(
        `${year}/${fillZero(month, 2)}/${PART_PERIOD[BME_PART.MIDDLE].START}`,
        "YYYY-MM-DD"
      );
      break;
    case BME_PART.MIDDLE:
      // NOTE: 中旬から下旬
      nextDate = dayjs(
        `${year}/${fillZero(month, 2)}/${PART_PERIOD[BME_PART.END].START}`,
        "YYYY-MM-DD"
      );
      break;
    case BME_PART.END:
      if (month === 12) {
        // NOTE: 翌年の1月初旬
        nextDate = dayjs(`${year + 1}/01/01`, "YYYY-MM-DD");
      } else {
        // NOTE: 翌月の初旬
        nextDate = dayjs(`${year}/${fillZero(month + 1, 2)}/01`, "YYYY-MM-DD");
      }
      break;
  }
  return nextDate;
}

export function getDiffOfParts(
  startEventDate: EventDate,
  endEventDate: EventDate
) {
  const isInvalidDate = endEventDate.date.diff(startEventDate.date) < 0;
  if (isInvalidDate) {
    return null;
  }
  let pointer = cloneDeep(startEventDate);
  let diffParts: EventDate[] = [pointer];
  let isReachedToEnd = isSamePeriod(pointer, endEventDate);

  while (!isReachedToEnd) {
    pointer = cloneDeep(pointer);
    if (pointer.part === BME_PART.END) {
      pointer.date = pointer.date
        .add(1, "month")
        .date(PART_PERIOD[BME_PART.BEGINNING].START);
      pointer = getEventDateFromDate(pointer.date);
    } else if (pointer.part === BME_PART.MIDDLE) {
      pointer.part = BME_PART.END;
      pointer.day = PART_PERIOD[BME_PART.END].START;
    } else {
      pointer.part = BME_PART.MIDDLE;
      pointer.day = PART_PERIOD[BME_PART.MIDDLE].START;
    }
    isReachedToEnd = isSamePeriod(pointer, endEventDate);
    diffParts.push(pointer);
  }
  return diffParts;
}

export function getEventWidth(
  startEventDate: EventDate,
  endEventDate: EventDate,
  partWidth: number
) {
  const diffOfParts = getDiffOfParts(startEventDate, endEventDate);
  if (!diffOfParts || !partWidth) {
    return 0;
  }
  const eventWidth = diffOfParts.length * partWidth;
  return eventWidth - EVENT_STYLES.MARGIN.LEFT - EVENT_STYLES.MARGIN.RIGHT;
}

export function getEventPositionX(
  startPlanEventDate: EventDate,
  startEventDate: EventDate,
  partWidth: number
) {
  const diffOfParts = getDiffOfParts(startPlanEventDate, startEventDate);
  if (!diffOfParts) {
    return 0;
  }
  const positionX = (diffOfParts.length - 1) * partWidth;
  return positionX + EVENT_STYLES.MARGIN.LEFT;
}

export function getEventPositionYList(events: Application.EventProperties[]) {
  let y = 0;
  const clonedEvents = cloneDeep(events);
  const positionYList: { [eventId: number]: number } = {};
  const sortedByStartDateEvents = clonedEvents.sort((a, b) => {
    if (a.startDate.isAfter(b.startDate)) {
      return 1;
    } else if (a.startDate.isBefore(b.startDate)) {
      return -1;
    }
    return 0;
  });

  // NOTE: すべてのイベントのyを算出するまで繰り返し
  while (sortedByStartDateEvents.length) {
    // NOTE: 先頭のイベント（時期が最も早い）を取得
    let currentEvent = sortedByStartDateEvents.shift();
    while (currentEvent) {
      // NOTE: y座標を設定
      positionYList[currentEvent.id] =
        y * EVENT_STYLES.HEIGHT +
        y * EVENT_STYLES.MARGIN.TOP +
        EVENT_STYLES.MARGIN.BOTTOM +
        TIMELINE_STYLES.PADDING.TOP;
      // NOTE: currentEventより1つ先の旬にあるイベントを取得
      const nextEventIndex = sortedByStartDateEvents.findIndex(event => {
        if (!currentEvent) {
          return false;
        }
        return isFutureBME(
          getEventDateFromDate(event.startDate),
          getEventDateFromDate(currentEvent.endDate)
        );
      });
      currentEvent =
        nextEventIndex > -1
          ? sortedByStartDateEvents.splice(nextEventIndex, 1)[0]
          : undefined;
    }
    // NOTE: イベントが見つからない場合は次の行へ
    y++;
  }
  return positionYList;
}

export function initRecruitmentEvent(
  event: ApiResponse.RecruitmentEvent
): Application.RecruitmentEvent {
  return {
    ...event,
    position: {
      width: 0,
      height: 0,
      x: 0,
      y: 0
    },
    startDate: dayjs(event.start_at, "YYYY-MM-DD"),
    endDate: dayjs(event.end_at, "YYYY-MM-DD")
  };
}

export function isRecruitmentEventInPlan(
  event: Application.RecruitmentEvent,
  planStartDate: dayjs.Dayjs,
  planEndDate: dayjs.Dayjs
) {
  const isOutOfPlan =
    event.startDate.isAfter(planEndDate) ||
    event.startDate.isSame(planEndDate) ||
    event.endDate.isBefore(planStartDate) ||
    event.endDate.isSame(planStartDate);
  return !isOutOfPlan;
}

export function getRecruitmentEventPosition(
  planStartDate: dayjs.Dayjs,
  planEndDate: dayjs.Dayjs,
  eventStartDate: dayjs.Dayjs,
  eventEndDate: dayjs.Dayjs,
  positionY: number,
  partWidth: number
): Application.Position {
  // NOTE: タイムライン期間外の場合は計算対象外となるため、タイムラインの期間を基準にする
  const startDate =
    eventStartDate.isAfter(planStartDate) ||
    eventStartDate.isSame(planStartDate)
      ? eventStartDate
      : planStartDate;
  const endDate =
    eventEndDate.isBefore(planEndDate) || eventEndDate.isSame(planEndDate)
      ? eventEndDate
      : planEndDate;
  return {
    x: getEventPositionX(
      getEventDateFromDate(planStartDate),
      getEventDateFromDate(startDate),
      partWidth
    ),
    y: positionY,
    width: getEventWidth(
      getEventDateFromDate(startDate),
      getEventDateFromDate(endDate),
      partWidth
    ),
    height: EVENT_STYLES.HEIGHT
  };
}

export function sortEventToMoveTab(
  a: Application.EventProperties,
  b: Application.EventProperties
) {
  // NOTE: x座標の昇順（左から右に向かって）ソート
  if (a.position.x > b.position.x) return 1;
  else if (a.position.x < b.position.x) return -1;
  // NOTE: y座標の昇順（上から下に向かって）ソート
  if (a.position.y > b.position.y) return 1;
  else if (a.position.y < b.position.y) return -1;
  return 0;
}

export function calculateRecruitmentEvents(
  plan: ApiResponse.PlanDetail,
  planStartDate: dayjs.Dayjs,
  planEndDate: dayjs.Dayjs,
  partWidth: number
): Application.PlanDetail {
  const applicationEvents: Application.RecruitmentEvent[] = plan.recruitment_events
    .map(initRecruitmentEvent)
    .filter(event =>
      isRecruitmentEventInPlan(event, planStartDate, planEndDate)
    );
  const positionYList = getEventPositionYList(applicationEvents);
  const applicationPlan: Application.PlanDetail = {
    ...plan,
    planStartDate,
    planEndDate,
    recruitment_events_with_position: applicationEvents
      .map(event => {
        return {
          ...event,
          position: getRecruitmentEventPosition(
            planStartDate,
            planEndDate,
            event.startDate,
            event.endDate,
            positionYList[event.id],
            partWidth
          )
        };
      })
      .sort(sortEventToMoveTab)
  };
  return applicationPlan;
}

/*
NOTE:
他社イベントの開始〜終了時期を採用計画の期間内に収まるように整形する
*/
export function sliceCompetitorEventInPlan(
  planStartDate: dayjs.Dayjs,
  planEndDate: dayjs.Dayjs,
  event: ApiResponse.CompetitorEvent | ApiResponse.StudentEvent
) {
  const formattedEvent = cloneDeep(event);
  const shouldFormatDate = {
    start: false,
    end: false
  };
  const eventStartDate = dayjs(
    `${event.start_at.year}-${("0" + event.start_at.month).slice(-2)}-01`,
    "YYYY-MM-DD"
  );
  const eventEndDate = dayjs(
    `${event.end_at.year}-${("0" + event.end_at.month).slice(-2)}-01`,
    "YYYY-MM-DD"
  );
  // NOTE: イベントの開始時期が採用計画の開始時期より前だが、イベントの終了時期が採用計画の期間内の場合、イベントの開始時期をずらす
  if (
    eventStartDate.isBefore(planStartDate) &&
    (eventEndDate.isAfter(planStartDate) ||
      eventEndDate.isSame(planStartDate)) &&
    (eventEndDate.isBefore(planEndDate) || eventEndDate.isSame(planEndDate))
  ) {
    shouldFormatDate.start = true;
  }
  // NOTE: イベントの終了時期が採用計画の終了時期より後だが、イベントの開始時期が採用計画の期間内の場合、イベントの終了時期をずらす
  if (
    (eventStartDate.isAfter(planStartDate) ||
      eventStartDate.isSame(planStartDate)) &&
    (eventStartDate.isBefore(planEndDate) ||
      eventStartDate.isSame(planEndDate)) &&
    eventEndDate.isAfter(planEndDate)
  ) {
    shouldFormatDate.end = true;
  }
  // NOTE: イベントの開始〜終了時期が採用計画の開始〜終了時期を超える期間の場合は採用計画に合わせる
  if (
    eventStartDate.isBefore(planStartDate) &&
    eventEndDate.isAfter(planEndDate)
  ) {
    shouldFormatDate.start = true;
    shouldFormatDate.end = true;
  }
  if (shouldFormatDate.start) {
    formattedEvent.start_at = {
      type: formattedEvent.end_at.type,
      year: planStartDate.year(),
      month: planStartDate.month() + 1,
      part: BME_PART.BEGINNING,
      day: 1
    };
  }
  if (shouldFormatDate.end) {
    formattedEvent.end_at = {
      type: formattedEvent.end_at.type,
      year: planEndDate.year(),
      month: planEndDate.month() + 1,
      part: BME_PART.END,
      day: 1
    };
  }
  return formattedEvent;
}

export function getCompetitorEventDateList(
  events: Application.CompetitorEvent[],
  timelineDateList: TimelineDateList
): CompetitorEventDateList {
  return events.reduce<CompetitorEventDateList>((accumulator, event) => {
    const formattedEvent = sliceCompetitorEventInPlan(
      timelineDateList[event.graduate_year].start,
      timelineDateList[event.graduate_year].end,
      event
    );
    const formattedStartEventDate = getEventDateFromBME(
      formattedEvent.start_at
    );
    const formattedEndEventDate = getEventDateFromBME(formattedEvent.end_at);
    accumulator[event.id] = {
      start: formattedStartEventDate,
      end: formattedEndEventDate
    };
    return accumulator;
  }, {});
}
export function getStudentEventDateList(
  events: Application.StudentEvent[],
  timelineDateList: TimelineDateList,
  graduate_year: number
): CompetitorEventDateList {
  return events.reduce<CompetitorEventDateList>((accumulator, event) => {
    const formattedEvent = sliceCompetitorEventInPlan(
      timelineDateList[graduate_year].start,
      timelineDateList[graduate_year].end,
      event
    );
    const formattedStartEventDate = getEventDateFromBME(
      formattedEvent.start_at
    );
    const formattedEndEventDate = getEventDateFromBME(formattedEvent.end_at);
    accumulator[event.id] = {
      start: formattedStartEventDate,
      end: formattedEndEventDate
    };
    return accumulator;
  }, {});
}

export function getCompetitorEventPosition(
  competitorEventDate: CompetitorEventDate,
  planStartDate: EventDate,
  positionY: number,
  partWidth: number
): Application.Position {
  return {
    width: getEventWidth(
      competitorEventDate.start.from,
      competitorEventDate.end.to,
      partWidth
    ),
    height: EVENT_STYLES.HEIGHT,
    x: getEventPositionX(
      planStartDate,
      competitorEventDate.start.from,
      partWidth
    ),
    y: positionY
  };
}

export function isValidEvent(
  event: Application.CompetitorEvent | Application.StudentEvent
) {
  const isValidTerm = event.startDate.isBefore(event.endDate);

  return isValidTerm;
}

export function isEventInPlan(
  eventDate: CompetitorEventDate,
  timelineDate: TimelineDate
) {
  return (
    (eventDate.start.from.date.isAfter(timelineDate.start) ||
      eventDate.start.from.date.isSame(timelineDate.start)) &&
    (eventDate.end.to.date.isBefore(timelineDate.end) ||
      eventDate.end.to.date.isSame(timelineDate.end))
  );
}

export function createApplicationCompetitorEmbedded(
  competitor: ApiResponse.CompetitorEmbedded,
  eventPartWidth: number
): Application.CompetitorEmbedded {
  const clonedCompetitor = cloneDeep(competitor);
  const timelineDateList = getTimelineDateListForCompetitors(competitor.events);
  const timelineStartDateList = getTimelineStartEventDateList(timelineDateList);
  const competitorEvents = clonedCompetitor.events
    .map(event => competitorEvent.init(event))
    .filter(event => isValidEvent(event));
  const eventDateList = getCompetitorEventDateList(
    competitorEvents,
    timelineDateList
  );
  const filteredCompetitorEvents = competitorEvents.filter(event => {
    const eventDate = eventDateList[event.id];
    const timelineDate = timelineDateList[event.graduate_year];
    return isEventInPlan(eventDate, timelineDate);
  });
  const positionYList = getEventPositionYList(
    unifyYearsOfCompetitorEvents(filteredCompetitorEvents)
  );
  return {
    ...clonedCompetitor,
    events: filteredCompetitorEvents.map(event => ({
      ...event,
      position: getCompetitorEventPosition(
        eventDateList[event.id],
        timelineStartDateList[event.graduate_year],
        positionYList[event.id],
        eventPartWidth
      )
    }))
  };
}

export function createApplicationStudentEmbedded(
  student: ApiResponse.StudentEmbedded,
  graduate_year: number,
  eventPartWidth: number
): Application.StudentEmbedded {
  const clonedStudent = cloneDeep(student);
  const timelineDateList = getTimelineDateListForStudents(
    student.events,
    graduate_year
  );
  const timelineStartDateList = getTimelineStartEventDateList(timelineDateList);
  const studentEvents = clonedStudent.events
    .map(event => studentEvent.init(event))
    .filter(event => isValidEvent(event));
  const eventDateList = getStudentEventDateList(
    studentEvents,
    timelineDateList,
    graduate_year
  );
  const filteredStudentEvents = studentEvents.filter(event => {
    const eventDate = eventDateList[event.id];
    const timelineDate = timelineDateList[graduate_year];
    return isEventInPlan(eventDate, timelineDate);
  });
  const positionYList = getEventPositionYList(
    unifyYearsOfStudentEvents(filteredStudentEvents, graduate_year)
  );
  return {
    ...clonedStudent,
    events: filteredStudentEvents.map(event => ({
      ...event,
      position: getCompetitorEventPosition(
        eventDateList[event.id],
        timelineStartDateList[graduate_year],
        positionYList[event.id],
        eventPartWidth
      )
    }))
  };
}

const defaultPlan = {
  id: 0,
  name: "",
  graduate_year: 2000,
  created_at: "",
  updated_at: "",
  recruitment_events: [],
  competitors: [],
  student_segments: []
};

export function createApplicationPlan(
  plan: ApiResponse.PlanDetail,
  eventPartWidth: number
): Application.PlanDetail {
  const clonedPlan = cloneDeep({ ...defaultPlan, ...(plan || {}) });
  const planDate = getTimelineDateFromGraduateYear(plan.graduate_year);
  const applicationPlan = calculateRecruitmentEvents(
    clonedPlan,
    planDate.start,
    planDate.end,
    eventPartWidth
  );

  const sortedStudentSegmentsApplicationPlan = sortStudentSegments(
    applicationPlan
  );

  return sortedStudentSegmentsApplicationPlan;
}

const sortStudentSegments = (
  plan: Application.PlanDetail
): Application.PlanDetail => {
  return {
    ...plan,
    student_segments: plan.student_segments
  };
};

type PositionInWindow = {
  verticalPosition: "top" | "center" | "bottom";
  horizontalPosition: "left" | "center" | "right";
};

function getPositionInWindow(targetRect: DOMRect): PositionInWindow {
  const verticalCenterOfWindow = Math.round(window.innerHeight / 2);
  const horizontalCenterOfWindow = Math.round(window.innerWidth / 2);
  const topOfTarget = Math.round(targetRect.top + targetRect.height / 2);
  const leftOfTarget = Math.round(targetRect.left + targetRect.width / 2);
  const verticalPositionOfTarget =
    verticalCenterOfWindow === topOfTarget
      ? "center"
      : verticalCenterOfWindow > topOfTarget
      ? "top"
      : "bottom";
  const horizontalPositionOfTarget =
    horizontalCenterOfWindow === leftOfTarget
      ? "center"
      : horizontalCenterOfWindow > leftOfTarget
      ? "left"
      : "right";
  return {
    verticalPosition: verticalPositionOfTarget,
    horizontalPosition: horizontalPositionOfTarget
  };
}

export function calculateSpeechBubblePosition(
  $trigger: Element,
  $speechBubble: Element
): CSS.Positions {
  const speechBubblePosition = {
    top: "auto",
    left: "auto",
    bottom: "auto",
    right: "auto"
  };
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  const triggerRect = $trigger.getBoundingClientRect();
  const positionInWindow = getPositionInWindow(triggerRect as DOMRect);
  const speechBubbleRect = $speechBubble.getBoundingClientRect();
  const isOverflowedFromWindow = {
    top: triggerRect.top - speechBubbleRect.height < 0,
    left: triggerRect.left < speechBubbleRect.width / 2,
    bottom:
      windowHeight - triggerRect.top - triggerRect.height <
      speechBubbleRect.height,
    right:
      windowWidth - triggerRect.left - triggerRect.width / 2 <
      speechBubbleRect.width / 2
  };
  if (isOverflowedFromWindow.right) {
    speechBubblePosition.right = `${SPEECH_BUBBLE_STYLES.MARGIN.HORIZONTAL}px`;
  } else if (isOverflowedFromWindow.left) {
    speechBubblePosition.left = `${SPEECH_BUBBLE_STYLES.MARGIN.HORIZONTAL}px`;
  } else {
    let diffWidth = triggerRect.width - speechBubbleRect.width;
    speechBubblePosition.left = `${triggerRect.left + diffWidth / 2}px`;
  }
  if (positionInWindow.verticalPosition === "bottom") {
    speechBubblePosition.bottom = `${windowHeight -
      triggerRect.top +
      SPEECH_BUBBLE_STYLES.MARGIN.VERTICAL}px`;
  } else {
    speechBubblePosition.top = `${triggerRect.top +
      triggerRect.height +
      SPEECH_BUBBLE_STYLES.MARGIN.VERTICAL}px`;
  }

  return speechBubblePosition;
}

export function calculateTimelineHeight(
  events: Application.EventProperties[],
  minHeight: number = 0
) {
  const maxYOfEvents = Math.max(...events.map(event => event.position.y));
  const computedTimelineHeight =
    maxYOfEvents + EVENT_STYLES.HEIGHT + TIMELINE_STYLES.PADDING.BOTTOM;
  return Math.max(computedTimelineHeight, minHeight);
}

export function savePlanSettings(
  params: PlanState["planSettings"],
  planId: number
) {
  const planSettingsJson = localStorage.getItem(planSettingsKey) || "{}";
  const prePlanSettings = JSON.parse(planSettingsJson);

  const nextPlanSettings = { ...prePlanSettings, [planId]: params };

  localStorage.setItem(planSettingsKey, JSON.stringify(nextPlanSettings));
}

export function getPlanSettings(planId: number): PlanState["planSettings"] {
  const settingsJson = localStorage.getItem(planSettingsKey) || "{}";
  const prePlanSettings = JSON.parse(settingsJson);
  return prePlanSettings[planId];
}

export function saveInitializeGraduateYears(
  graduateYears: number[],
  planId: number
) {
  const graduateYearsJson = localStorage.getItem(planGraduateYearsKey) || "{}";
  let newGraduateYears = JSON.parse(graduateYearsJson);

  newGraduateYears[planId] = graduateYears;

  localStorage.setItem(planGraduateYearsKey, JSON.stringify(newGraduateYears));
}

export function saveGraduateYears(graduateYear: number, planId: number) {
  const graduateYearsJson = localStorage.getItem(planGraduateYearsKey) || "{}";
  let graduateYears = JSON.parse(graduateYearsJson);

  if (graduateYears[planId].includes(graduateYear)) {
    const index = graduateYears[planId].indexOf(graduateYear);
    graduateYears[planId].splice(index, 1);
  } else {
    graduateYears[planId].push(graduateYear);
  }

  localStorage.setItem(planGraduateYearsKey, JSON.stringify(graduateYears));

  return graduateYears[planId];
}

export function getGraduateYears(
  planId: number
): PlanState["selectedGraduateYears"] {
  const GraduateYearsJson = localStorage.getItem(planGraduateYearsKey) || "{}";
  const preGraduateYears = JSON.parse(GraduateYearsJson);
  return preGraduateYears[planId];
}

export function moveArrayIndex(
  array: any[],
  oldIndex: number,
  newIndex: number
) {
  const targetItem = array[oldIndex];

  array.splice(oldIndex, 1);
  array.splice(newIndex, 0, targetItem);

  return array;
}
